'use strict';

const CONTAINER_SELECTOR = '.set-items orch-product-set-container';
const SYNC_DEBOUNCE_MS = 250;
const ADDED_MODAL_SELECTOR = '.js-set-added-modal';
const MODAL_CONTENT_SELECTOR = '.js-set-added-modal-tpl';
const SIZE_GUIDE_MODAL_SELECTOR = '.size-guide-modal';
const STICKY_CTA_SELECTOR = '.product-set-add-to-cart-wrapper';
const FOOTER_SELECTOR = 'footer.footer';
const STICKY_CTA_MEDIA_QUERY = '(max-width: 768.98px)';
const MODAL_RENDER_DELAY_MS = 50;
const PAGE_SELECTOR = '.product-set-detail';
const LAYOUT_SELECTOR = '.product-set-layout';
const SITE_HEADER_SELECTOR = '.site-header';
const TOP_OFFSET_PROPERTY = '--set-top-offset';

let lastModalTrigger = null;

let modalContentHtml = null;

/**
 * Reads once the server-rendered modal content from its inert <template>.
 * @returns {string} - the modal inner HTML, or '' when the template is missing
 */
function getModalContentHtml() {
    if (modalContentHtml === null) {
        const tpl = document.querySelector(MODAL_CONTENT_SELECTOR);
        modalContentHtml = tpl ? tpl.innerHTML : '';
    }

    return modalContentHtml;
}

/**
 * Makes slick recompute the recommendation carousel once the modal is visible.
 * @param {HTMLElement} modalEl - the orch-modal element
 */
function refreshModalRecommendations(modalEl) {
    setTimeout(function () {
        const $grid = $(modalEl).find('.product-recommendations .product-grid');

        if (typeof $.fn.slick === 'function' && $grid.hasClass('slick-initialized')) {
            $grid.slick('setPosition');
        }
    }, MODAL_RENDER_DELAY_MS);
}

/**
 * Opens the "items added to cart" DS modal.
 * @param {jQuery} $trigger - the add-to-cart button that triggered the add
 */
function openAddedToCartModal($trigger) {
    const modalEl = document.querySelector(ADDED_MODAL_SELECTOR);

    if (!modalEl) {
        return;
    }

    modalEl.modalContent = getModalContentHtml();

    lastModalTrigger = $trigger && $trigger.length ? $trigger : null;

    modalEl.isOpen = true;

    refreshModalRecommendations(modalEl);
}

/**
 * Wires the added-to-cart modal controls, once per page.
 */
function bindAddedModalControls() {
    const modalEl = document.querySelector(ADDED_MODAL_SELECTOR);

    if (!modalEl || modalEl.getAttribute('data-controls-bound')) {
        return;
    }

    modalEl.setAttribute('data-controls-bound', 'true');

    $(modalEl).on('click', '.js-set-added-modal-continue', function () {
        modalEl.isOpen = false;
    });

    modalEl.addEventListener('closeModal', function () {
        modalEl.isOpen = false;

        if (lastModalTrigger && lastModalTrigger.length) {
            lastModalTrigger.trigger('focus');
            lastModalTrigger = null;
        }
    });
}

/**
 * Publishes the layout's position in the page so its height fits the screen.
 * @returns {void}
 */
function initViewportFit() {
    const page = document.querySelector(PAGE_SELECTOR);
    const layout = page ? page.querySelector(LAYOUT_SELECTOR) : null;

    if (!layout) {
        return;
    }

    /**
     * Writes the layout's top edge, in page coordinates, on the page container.
     */
    function update() {
        const top = layout.getBoundingClientRect().top + window.pageYOffset;

        page.style.setProperty(TOP_OFFSET_PROPERTY, Math.round(top) + 'px');
    }

    update();
    window.addEventListener('resize', update);
    window.addEventListener('load', update);

    const header = document.querySelector(SITE_HEADER_SELECTOR);

    if (header && typeof window.ResizeObserver === 'function') {
        new window.ResizeObserver(update).observe(header);
    }
}

/**
 * Keeps the sticky Total/CTA bar from covering the footer on mobile.
 * @returns {void}
 */
function initStickyCtaFooterGuard() {
    const cta = document.querySelector(STICKY_CTA_SELECTOR);
    const footer = document.querySelector(FOOTER_SELECTOR);

    if (!cta || !footer || typeof window.IntersectionObserver !== 'function') {
        return;
    }

    const mediaQuery = window.matchMedia(STICKY_CTA_MEDIA_QUERY);
    let ticking = false;
    let watching = false;

    /**
     * Aligns the CTA block on the footer's top edge.
     */
    function update() {
        ticking = false;

        if (!mediaQuery.matches) {
            cta.style.transform = '';
            return;
        }

        const overlap = window.innerHeight - footer.getBoundingClientRect().top;

        cta.style.transform = overlap > 0 ? 'translateY(-' + overlap + 'px)' : '';
    }

    /**
     * Schedules an update on the next frame.
     */
    function requestUpdate() {
        if (!ticking) {
            ticking = true;
            window.requestAnimationFrame(update);
        }
    }

    /**
     * Turns scroll tracking on or off depending on how close the footer is.
     * @param {boolean} shouldWatch - true when the footer is coming into view
     */
    function setWatching(shouldWatch) {
        if (shouldWatch === watching) {
            return;
        }

        watching = shouldWatch;

        if (shouldWatch) {
            window.addEventListener('scroll', requestUpdate, { passive: true });
            window.addEventListener('resize', requestUpdate);
            requestUpdate();
        } else {
            window.removeEventListener('scroll', requestUpdate);
            window.removeEventListener('resize', requestUpdate);
            cta.style.transform = '';
        }
    }

    new window.IntersectionObserver(function (entries) {
        setWatching(entries[0].isIntersecting);
    }, { rootMargin: '100% 0px 0px 0px' }).observe(footer);

    if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', update);
    }
}

/**
 * Builds the selection payload sent to Product-SetState.
 * @param {Array<Object>} items - local ProductSetItemState[] copy
 * @returns {Array<Object>} - [{id, selected, color, size}]
 */
function toStatePayload(items) {
    return items.map(function (state) {
        return {
            id: state.item.id,
            selected: !!state.selected && state.item.availability !== 'outOfStock',
            color: state.selectedColor || null,
            size: state.selectedSize || null
        };
    });
}

/**
 * Hydrates an orch-product-set-container and keeps the total in sync with the selection.
 * @param {HTMLElement} el - orch-product-set-container element
 */
function initContainer(el) {
    const rawItems = el.getAttribute('items');

    if (!rawItems) {
        return;
    }

    let items;

    try {
        items = JSON.parse(rawItems);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('orch-product-set-container: invalid items JSON', e);
        return;
    }

    el.items = items;

    const $el = $(el);
    const $container = $el.closest('.set-items');
    const $panel = $container.closest('.product-set-panel');
    const stateUrl = $container.data('set-state-url');
    const $totalPrice = $panel.find('.set-add-to-cart orch-price');
    const $addToCart = $panel.find('.js-set-add-to-cart');
    const $message = $panel.find('.js-set-add-to-cart-message');

    let resolvedPids = {};
    let syncTimer = null;
    let syncInFlight = false;
    let syncFailed = false;
    let syncCallbacks = [];

    /**
     * Merges a patch into one item's state and pushes a fresh array to the element.
     * @param {string} itemId - id of the set item to patch
     * @param {Object} patch - fields to merge into that item's state
     */
    function patchItemState(itemId, patch) {
        items = items.map(function (state) {
            if (state.item.id !== itemId) {
                return state;
            }

            return { ...state, ...patch };
        });

        el.items = items;
    }

    /**
     * Finds the entry the server returned for a given set item.
     * @param {Array<Object>} entries - items[] of the Product-SetState response
     * @param {string} itemId - id of the set item being refreshed
     * @returns {Object|null} - matching entry, or null when the server did not
     *     describe that item
     */
    function findServerEntry(entries, itemId) {
        for (const entry of entries) {
            if (entry.id === itemId) {
                return entry;
            }
        }

        return null;
    }

    /**
     * Builds the fields that changed between a server entry and the current item.
     * @param {Object} entry - server entry for this item
     * @param {Object} item - the item's current state
     * @returns {Object} - fields to merge, empty when nothing changed
     */
    function buildItemPatch(entry, item) {
        const patch = {};

        if (entry.imageUrl && entry.imageUrl !== item.imageUrl) {
            patch.imageUrl = entry.imageUrl;
        }

        if (entry.productUrl && entry.productUrl !== item.productUrl) {
            patch.productUrl = entry.productUrl;
        }

        if (entry.sizes) {
            patch.sizes = entry.sizes;
        }

        if (entry.colors) {
            patch.colors = entry.colors;
        }

        return patch;
    }

    /**
     * Shows a feedback message under the CTA (error styling by default).
     * @param {string} text - message to display; empty hides the zone
     * @param {boolean} isError - true for error styling, false for success
     */
    function showMessage(text, isError) {
        if (!text) {
            $message.addClass('d-none').empty();
            return;
        }

        $message
            .toggleClass('o-text-error-base', isError)
            .removeClass('d-none')
            .text(text);
    }

    /**
     * Runs the callbacks queued by whenSynced.
     */
    function runSyncCallbacks() {
        const callbacks = syncCallbacks;

        syncCallbacks = [];
        callbacks.forEach(function (callback) {
            callback();
        });
    }

    /**
     * Pushes the current selection to the server and refreshes the total.
     */
    function syncState() {
        if (!stateUrl) {
            syncFailed = true;
            runSyncCallbacks();
            return;
        }

        syncInFlight = true;

        $.ajax({
            url: stateUrl,
            method: 'POST',
            data: { items: JSON.stringify(toStatePayload(items)) },
            complete: function () {
                syncInFlight = false;
                runSyncCallbacks();
            },
            error: function () {
                syncFailed = true;
            },
            success: function (data) {
                if (!data || data.error) {
                    syncFailed = true;
                    return;
                }

                syncFailed = false;
                resolvedPids = {};
                let updatedItems = false;

                (data.items || []).forEach(function (entry) {
                    resolvedPids[entry.id] = entry.pid && entry.orderable ? entry.pid : null;
                });

                if (data.items && data.items.length > 0) {
                    items = items.map(function (state) {
                        const entry = findServerEntry(data.items, state.item.id);
                        const patch = entry ? buildItemPatch(entry, state.item) : {};

                        if (Object.keys(patch).length > 0) {
                            updatedItems = true;

                            return { ...state, item: { ...state.item, ...patch } };
                        }

                        return state;
                    });

                    if (updatedItems) {
                        el.items = items;
                    }
                }

                if (data.total) {
                    $totalPrice.attr('price', JSON.stringify(data.total));
                }
            }
        });
    }

    /**
     * Debounces syncState.
     */
    function scheduleSync() {
        if (syncTimer) {
            clearTimeout(syncTimer);
        }

        syncTimer = setTimeout(function () {
            syncTimer = null;
            syncState();
        }, SYNC_DEBOUNCE_MS);
    }

    /**
     * Runs done() once resolvedPids reflects the current selection.
     * @param {Function} done - callback to run once the state is in sync
     */
    function whenSynced(done) {
        if (syncTimer) {
            clearTimeout(syncTimer);
            syncTimer = null;
            syncCallbacks.push(done);
            syncState();
            return;
        }

        if (syncInFlight) {
            syncCallbacks.push(done);
            return;
        }

        done();
    }

    /**
     * Builds the Cart-AddProduct pidsObj payload, or null while a selected item has no resolvable variant.
     * @returns {Array<Object>|null} - [{pid, qty, options}], or null if invalid
     */
    function buildPidsObj() {
        const pidsObj = [];
        let incomplete = false;
        let selectedCount = 0;

        items.forEach(function (state) {
            if (!state.selected) {
                return;
            }

            selectedCount++;

            if (state.item.availability === 'outOfStock') {
                return;
            }

            const pid = resolvedPids[state.item.id];

            if (!pid) {
                incomplete = true;
                return;
            }

            pidsObj.push({ pid: pid, qty: '1', options: '[]' });
        });

        if (selectedCount === 0) {
            return { pidsObj: null, reason: 'empty' };
        }

        if (incomplete || pidsObj.length === 0) {
            return { pidsObj: null, reason: 'incomplete' };
        }

        return { pidsObj: pidsObj, reason: null };
    }

    $el.on('toggleSelect', function (event) {
        const detail = event.originalEvent.detail;
        patchItemState(detail.itemId, { selected: detail.checked });
        showMessage('', true);
        scheduleSync();
    });

    $el.on('selectColor', function (event) {
        const detail = event.originalEvent.detail;
        patchItemState(detail.itemId, { selectedColor: detail.color.id });
        scheduleSync();
    });

    $el.on('selectSize', function (event) {
        const detail = event.originalEvent.detail;
        patchItemState(detail.itemId, { selectedSize: detail.size.id });
        showMessage('', true);
        scheduleSync();
    });

    $el.on('sizeGuideClick', function () {
        const $sizeGuide = $(SIZE_GUIDE_MODAL_SELECTOR);

        if ($sizeGuide.length) {
            $sizeGuide.modal('show');
        }
    });

    /**
     * Sends the current selection to Cart-AddProduct.
     * @param {jQuery} $button - the CTA that was clicked
     */
    function submitAddToCart($button) {
        const addToCartUrl = $button.data('add-to-cart-url');
        const built = buildPidsObj();

        if (!built.pidsObj) {
            showMessage(built.reason === 'empty'
                ? $button.data('selection-empty-msg')
                : $button.data('selection-required-msg'), true);
            $.spinner().stop();
            return;
        }

        const pidsObj = built.pidsObj;

        showMessage('', true);

        $.ajax({
            url: addToCartUrl,
            method: 'POST',
            data: {
                pid: $button.data('pid'),
                pidsObj: JSON.stringify(pidsObj)
            },
            success: function (data) {
                if (!data || data.is_error || data.error) {
                    showMessage(data && data.message ? data.message : '', true);
                    $.spinner().stop();
                    return;
                }

                $('.minicart').trigger('count:update', data);
                $('body').trigger('product:afterAddToCart', data);

                if (data.reportingURL) {
                    $.ajax({ url: data.reportingURL, method: 'GET' });
                }

                $.spinner().stop();
                openAddedToCartModal($button);
            },
            error: function () {
                showMessage($button.data('sync-error-msg'), true);
                $.spinner().stop();
            }
        });
    }

    $addToCart.on('click', function () {
        const $button = $(this);

        $.spinner().start();

        whenSynced(function () {
            if (syncFailed) {
                showMessage($button.data('sync-error-msg'), true);
                $.spinner().stop();
                return;
            }

            submitAddToCart($button);
        });
    });

    syncState();
}

module.exports = {
    init: function () {
        $(CONTAINER_SELECTOR).each(function () {
            initContainer(this);
        });

        bindAddedModalControls();
        initViewportFit();
        initStickyCtaFooterGuard();
    }
};
